import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, RefreshCw, Loader2 } from 'lucide-react';
import MizanReading from './MizanReading';
import UploadedTrialBalanceFilesList from './UploadedTrialBalanceFilesList';
import { useLanguage } from '../../contexts/LanguageContext';
import { SectionHeader } from '../../components/common/SectionHeader';

const TrialBalanceFromFileTab = ({ uploadedMizansRefreshToken = 0 }) => {
  const { t } = useLanguage();
  const [listToolbar, setListToolbar] = useState({ onRefresh: null, loading: false });
  const [externalViewingMizanId, setExternalViewingMizanId] = useState(null);
  const [externalViewingFileName, setExternalViewingFileName] = useState('');
  const [externalViewingDonem, setExternalViewingDonem] = useState(null);
  const [externalViewingDateRange, setExternalViewingDateRange] = useState(null);
  const [externalViewingPeriodStart, setExternalViewingPeriodStart] = useState(null);
  const [externalViewingPeriodEnd, setExternalViewingPeriodEnd] = useState(null);
  const [externalViewingRefreshToken, setExternalViewingRefreshToken] = useState(0);
  const [pendingAutoSelectLatest, setPendingAutoSelectLatest] = useState(false);

  const handleView = useCallback((selected) => {
    setExternalViewingMizanId(selected?.mizanId || null);
    setExternalViewingFileName(selected?.fileName || '');
    setExternalViewingDonem(selected?.donem ?? null);
    setExternalViewingDateRange(selected?.tarihAraligi ?? null);
    setExternalViewingPeriodStart(selected?.periodCoveredStart ?? null);
    setExternalViewingPeriodEnd(selected?.periodCoveredEnd ?? null);
    setExternalViewingRefreshToken((tok) => tok + 1);
  }, []);

  const handleDeleted = useCallback((deletedId) => {
    setExternalViewingMizanId((current) => (current === deletedId ? null : current));
  }, []);

  useEffect(() => {
    if (uploadedMizansRefreshToken > 0) {
      setPendingAutoSelectLatest(true);
    }
  }, [uploadedMizansRefreshToken]);

  const handleListLoaded = useCallback(
    (list) => {
      if (!pendingAutoSelectLatest) return;
      if (!Array.isArray(list) || list.length === 0) return;

      const latest = list[0];
      handleView({
        mizanId: latest?.mizanId,
        fileName: latest?.fileName || latest?.generatedFileName || '',
        donem: latest?.donem ?? null,
        tarihAraligi: latest?.tarihAraligi ?? null,
        periodCoveredStart: latest?.periodCoveredStart ?? latest?.periodStart ?? null,
        periodCoveredEnd: latest?.periodCoveredEnd ?? latest?.periodEnd ?? null,
      });
      setPendingAutoSelectLatest(false);
    },
    [pendingAutoSelectLatest, handleView]
  );

  const handleToolbarRender = useCallback((controls) => {
    setListToolbar(controls);
  }, []);

  return (
    <div className="space-y-10">
      <div>
        <SectionHeader
          icon={BookOpen}
          label={t('edefter.uploadedFilesTitle')}
          right={
            <button
              type="button"
              onClick={() => listToolbar.onRefresh?.()}
              disabled={listToolbar.loading || !listToolbar.onRefresh}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-white/10 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              {listToolbar.loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {t('edefter.refresh')}
            </button>
          }
        />
        <UploadedTrialBalanceFilesList
          refreshToken={uploadedMizansRefreshToken}
          onViewMizan={handleView}
          onDeletedMizanId={handleDeleted}
          onListLoaded={handleListLoaded}
          hideToolbar
          onToolbarRender={handleToolbarRender}
        />
      </div>

      <div>
        <MizanReading
          hideUploadedFilesList
          externalViewingMizanId={externalViewingMizanId}
          externalViewingFileName={externalViewingFileName}
          externalViewingDonem={externalViewingDonem}
          externalViewingDateRange={externalViewingDateRange}
          externalViewingPeriodStart={externalViewingPeriodStart}
          externalViewingPeriodEnd={externalViewingPeriodEnd}
          externalViewingRefreshToken={externalViewingRefreshToken}
          embedded
        />
      </div>
    </div>
  );
};

export default TrialBalanceFromFileTab;
